import { inject } from '@angular/core';
import type { MachineDatasheet } from '@indutrust/machine-model';
import { withFileUpload } from '@indutrust/shared-util';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { MachineCatalogApiClient } from '../machine-catalog-api-client.service';

type DatasheetContext = {
  contextEntryId: number;
};

type ContextStore = {
  contextEntryId: () => number;
};

/**
 * Modal-scoped store managing per-file upload/delete for
 * {@link MachineCatalogEntry} datasheets.
 *
 * Provided in the admin catalog form modal's `providers` so each modal
 * instance gets a fresh, isolated store.
 *
 * @example
 * const uploadStore = inject(MachineCatalogDatasheetStore);
 * uploadStore.setContext(entryId);
 * uploadStore.stageFiles([file]);
 * uploadStore.commit();
 */
export const MachineCatalogDatasheetStore = signalStore(
  withState<DatasheetContext>({
    contextEntryId: 0,
  }),
  withMethods((store) => ({
    /** Sets the catalog entry context used by `commit()` for upload/delete. */
    setContext(entryId: number): void {
      patchState(store, {
        contextEntryId: entryId,
      });
    },
  })),
  withFileUpload<MachineDatasheet>({
    upload: (store) => {
      const client = inject(MachineCatalogApiClient);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const ctx = store as ContextStore;
      return (file: File) => client.uploadDatasheet(ctx.contextEntryId(), file);
    },
    delete: (store) => {
      const client = inject(MachineCatalogApiClient);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const ctx = store as ContextStore;
      return (datasheetId: string) =>
        client.deleteDatasheet(ctx.contextEntryId(), Number(datasheetId));
    },
  })
);
